import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApiAuthed, useApiDev } from "../auth.js";
import { loadGeoSelection, type GeoSelection } from "../components/GeoSelector.js";
import type { OverviewResponse } from "../api.js";

interface PageProps {
  devModeNoAuth?: boolean;
}

interface SearchItem {
  label: string;
  detail: string;
  kind: "entity" | "signal" | "lookup" | "recent";
  to: string;
}

const RECENT_SEARCHES_KEY = "bios.recentSearches";
const LOOKUPS = ["Donald Trump", "Memphis", "Workforce", "Legal", "Capital deployment"];

export function SearchPage({ devModeNoAuth }: PageProps) {
  const api = devModeNoAuth ? useApiDev() : useApiAuthed();
  const [geo] = useState<GeoSelection>(() => loadGeoSelection());
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState<string[]>(() => loadRecentSearches());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .overview("7d", geo)
      .then((overview) => {
        if (alive) setData(overview);
      })
      .catch((err) => {
        if (alive) setError(String(err));
      });
    return () => {
      alive = false;
    };
  }, [api, geo]);

  const items = useMemo(() => buildSearchItems(data, recent), [data, recent]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) =>
      `${item.label} ${item.detail} ${item.kind}`.toLowerCase().includes(q),
    );
  }, [items, query]);

  function submitSearch() {
    const q = query.trim();
    if (!q) return;
    const next = [q, ...recent.filter((item) => item.toLowerCase() !== q.toLowerCase())].slice(0, 6);
    setRecent(next);
    saveRecentSearches(next);
  }

  return (
    <div className="bios-search-page">
      <header className="bios-search-header">
        <div>
          <div className="bios-search-kicker">Search</div>
          <p>Explore entities, places, modules, and signals.</p>
        </div>
        {geo.scope !== "national" && <span className="bios-search-geo">{geoLabel(geo)}</span>}
      </header>

      <div className="bios-search-box">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") submitSearch();
          }}
          placeholder="Search BIOS..."
          aria-label="Search BIOS"
        />
        <button type="button" onClick={submitSearch}>Search</button>
      </div>

      {error && <div className="bios-search-state">{error}</div>}

      <section className="bios-search-group">
        <h2>Explore</h2>
        <div className="bios-search-results">
          {filtered.slice(0, 18).map((item) => (
            <Link key={`${item.kind}:${item.label}:${item.to}`} to={item.to} className="bios-search-result">
              <span>{item.kind}</span>
              <strong>{item.label}</strong>
              <small>{item.detail}</small>
            </Link>
          ))}
        </div>
      </section>

      <SearchBottomNav active="search" />
      <div className="bios-feed-bottomnav-spacer" aria-hidden />
    </div>
  );
}

function buildSearchItems(data: OverviewResponse | null, recent: string[]): SearchItem[] {
  const recentItems = recent.map((label) => ({
    label,
    detail: "Recent search",
    kind: "recent" as const,
    to: `/app/command?q=${encodeURIComponent(label)}`,
  }));
  const lookupItems = LOOKUPS.map((label) => ({
    label,
    detail: label === "Memphis" ? "Geo lookup" : "Entity/module lookup",
    kind: "lookup" as const,
    to:
      label === "Workforce"
        ? "/app/intel#intel-workforce"
        : label === "Legal"
          ? "/app/intel#intel-legal"
          : `/app/command?q=${encodeURIComponent(label)}`,
  }));
  const entities = (data?.accountability?.actors ?? []).slice(0, 8).map((actor) => ({
    label: actor.name,
    detail: `${actor.role} · ${actor.module}`,
    kind: "entity" as const,
    to: `/app/intel#intel-${actor.module}`,
  }));
  const signals = [
    ...(data?.focus_recommendations ?? []).map((rec) => ({
      label: rec.label,
      detail: rec.reason,
      kind: "signal" as const,
      to: `/app/command?q=${encodeURIComponent(rec.query)}`,
    })),
    ...(data?.top_insights ?? []).map((insight) => ({
      label: insight.title,
      detail: insight.why_matters,
      kind: "signal" as const,
      to: `/app/intel#intel-${insight.module}`,
    })),
  ].slice(0, 10);
  return [...recentItems, ...lookupItems, ...entities, ...signals];
}

function SearchBottomNav({ active }: { active: "signals" | "command" | "intel" | "search" | "profile" }) {
  return (
    <nav className="bios-feed-bottomnav" aria-label="Primary navigation">
      <Link to="/app" className={`bios-feed-bottomnav-tab${active === "signals" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>◎</span><span className="bios-feed-bottomnav-label">Signals</span>
      </Link>
      <Link to="/app/command" className={`bios-feed-bottomnav-tab${active === "command" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>⌘</span><span className="bios-feed-bottomnav-label">Command</span>
      </Link>
      <Link to="/app/intel" className={`bios-feed-bottomnav-tab${active === "intel" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>◐</span><span className="bios-feed-bottomnav-label">Intel</span>
      </Link>
      <Link to="/app/search" className={`bios-feed-bottomnav-tab${active === "search" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>⌕</span><span className="bios-feed-bottomnav-label">Search</span>
      </Link>
      <Link to="/app/profile" className={`bios-feed-bottomnav-tab${active === "profile" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>◌</span><span className="bios-feed-bottomnav-label">Profile</span>
      </Link>
    </nav>
  );
}

function loadRecentSearches(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function saveRecentSearches(items: string[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(items));
}

const STATE_LABELS: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DC: "District of Columbia", DE: "Delaware",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan",
  MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

function geoLabel(geo: GeoSelection): string {
  if (geo.scope === "city" && geo.city) return geo.city;
  if (geo.scope === "state" && geo.state) return STATE_LABELS[geo.state] ?? geo.state;
  return "National";
}
